import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { InstitutionSelector } from '@/components/InstitutionSelector'
import { useAuth } from '@/hooks/useAuth'
import { useMobileSidebar } from '@/hooks/useMobileSidebar'
import { useInstitution } from '@/contexts/InstitutionContext'
import { useAdminInstitutions, DEFAULT_ADMIN_INSTITUTION_VALUE } from '@/lib/admin'
import { getMe } from '@/lib/api/auth.service'
import type { AuthGuard } from '@/lib/jwt'
import { getCircularAvatarUrl } from '@/lib/profile-media'
import styles from './AppHeader.module.css'

type DisplayUser = {
  name: string
  email?: string
  avatarUrl?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object'
}

function readUser(source: unknown): DisplayUser | null {
  if (!isRecord(source)) return null
  const name = typeof source.name === 'string' ? source.name : undefined
  const email = typeof source.email === 'string' ? source.email : undefined
  const avatarUrl = getCircularAvatarUrl(source) ?? undefined

  if (!name && !email && !avatarUrl) return null
  return { name: name ?? 'Usuário', email, avatarUrl }
}

function extractUserFromMe(guard: AuthGuard | null, me: unknown): DisplayUser | null {
  if (!me || !guard) return null
  if (!isRecord(me)) return null

  const candidates: unknown[] = []

  if (guard === 'admin') {
    candidates.push((me as Record<string, unknown>).admin)
    if (isRecord(me.data)) {
      candidates.push((me.data as Record<string, unknown>).admin)
      candidates.push(me.data)
    }
  }

  if (guard === 'donor') {
    candidates.push((me as Record<string, unknown>).donor)
    if (isRecord(me.data)) {
      candidates.push((me.data as Record<string, unknown>).donor)
      candidates.push(me.data)
    }
  }

  if (guard === 'institution') {
    candidates.push((me as Record<string, unknown>).institution)
    if (isRecord(me.data)) {
      candidates.push((me.data as Record<string, unknown>).institution)
      candidates.push(me.data)
    }
  }

  candidates.push((me as Record<string, unknown>).user)

  for (const candidate of candidates) {
    const user = readUser(candidate)
    if (user) return user
  }

  return readUser(me)
}

export function AppHeader() {
  const { guard, institution, me, isCollaborator, collaborator } = useAuth()
  const { isOpen, toggle } = useMobileSidebar()
  const { institutionId, setInstitutionId } = useInstitution()
  const navigate = useNavigate()
  const showInstitutionSelector = guard === 'admin'
  const { groups, loading } = useAdminInstitutions(showInstitutionSelector)
  const [selectedInstitutionUser, setSelectedInstitutionUser] = useState<DisplayUser | null>(null)

  const selectedInstitution = institutionId ?? DEFAULT_ADMIN_INSTITUTION_VALUE
  const handleInstitutionChange = (value: string) => {
    if (value === DEFAULT_ADMIN_INSTITUTION_VALUE) {
      setInstitutionId(null)
      navigate('/home-drftadmin', { replace: true })
    } else {
      setInstitutionId(value)
      navigate('/home', { replace: true })
    }
  }

  useEffect(() => {
    if (guard !== 'admin' || institutionId === null) {
      return
    }

    let cancelled = false

    const syncSelectedInstitution = () => {
      void getMe()
        .then((inst) => {
          if (cancelled) return
          setSelectedInstitutionUser({
            name: inst.name ?? 'Instituição',
            email: inst.email,
            avatarUrl: getCircularAvatarUrl(inst, '/logo/fiveicon.png') ?? '/logo/fiveicon.png',
          })
        })
        .catch(() => {
          if (!cancelled) setSelectedInstitutionUser(null)
        })
    }

    const handleProfileUpdated = () => {
      syncSelectedInstitution()
    }

    syncSelectedInstitution()
    window.addEventListener('legalprime:institution-profile-updated', handleProfileUpdated)

    return () => {
      cancelled = true
      window.removeEventListener('legalprime:institution-profile-updated', handleProfileUpdated)
    }
  }, [guard, institutionId])

  const user = useMemo<DisplayUser>(() => {
    if (guard === 'admin' && institutionId !== null && selectedInstitutionUser) {
      return selectedInstitutionUser
    }

    if (guard === 'institution' && isCollaborator && collaborator) {
      return {
        name: collaborator.name || 'Colaborador',
        email: collaborator.email,
        avatarUrl: getCircularAvatarUrl(institution, '/logo/fiveicon.png') ?? '/logo/fiveicon.png',
      }
    }

    if (guard === 'institution' && institution) {
      return {
        name: institution.name ?? 'Instituição',
        email: institution.email,
        avatarUrl: getCircularAvatarUrl(institution, '/logo/fiveicon.png') ?? '/logo/fiveicon.png',
      }
    }

    const fromMe = extractUserFromMe(guard, me)
    return (
      fromMe ?? {
        name: 'Usuário',
        email: 'usuario@exemplo.com',
        avatarUrl: '/logo/fiveicon.png',
      }
    )
  }, [collaborator, guard, institution, institutionId, isCollaborator, me, selectedInstitutionUser])

  const menuLabel = isOpen ? 'Fechar menu' : 'Abrir menu'

  return (
    <header
      className={[
        styles.header,
        !showInstitutionSelector && styles.headerNoSelector,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className={styles.menuButton}
        aria-label={menuLabel}
        aria-expanded={isOpen}
        aria-controls="app-sidebar"
        onClick={toggle}
      >
        <Icon icon={isOpen ? 'mdi:close' : 'mdi:menu'} width={24} height={24} />
      </button>
      {showInstitutionSelector && (
        <InstitutionSelector
          value={selectedInstitution}
          onChange={handleInstitutionChange}
          groups={groups}
          disabled={loading}
          className={styles.institutionSelector}
        />
      )}
      <Link
        to="/perfil"
        className={styles.profileCard}
        aria-label={`Perfil de ${user.name}`}
      >
        <div className={styles.avatar}>
          <img
            src={user.avatarUrl ?? '/logo/fiveicon.png'}
            alt=""
            className={styles.avatarImg}
          />
        </div>
        <div className={styles.profileInfo}>
          <span className={styles.profileName}>{user.name}</span>
          {user.email && <span className={styles.profileEmail}>{user.email}</span>}
        </div>
      </Link>
    </header>
  )
}
